import { FC, memo, useMemo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetFluidsQuery } from '../../services/coilApi';
import { useBotContext } from '../useBotContext';
import Select from '../atom/Select';
import { CoilSectionFieldsProps } from '../../models';
import type { IState } from '../../../../models';
import { bot_coil_types, getConnectionHandId } from '../../helpers/data/bot';

const CoilSectionFields: FC<CoilSectionFieldsProps> = ({
  section,
  onFieldChange,
  tunnelIndex,
  tunnel_configuration,
  airflow_direction_id,
  sectionIndex,
}) => {
  const { setValue } = useBotContext();
  const [isConnHandEnabled, setIsConnHandEnabled] = useState(false);
  const { coil_params: params, conn_hands } = useSelector(
    (state: IState) => state.coil,
  );
  const ahu_params = useSelector((state: IState) => state.ahu.ahu_params);

  const { data: fluidsData } = useGetFluidsQuery(
    {
      type_id: section.type_id!,
      duty_id: section.duty_id!,
      mfg_id: section.mfg_id!,
    },
    { skip: !section.duty_id || !section.type_id },
  );

  /** ---------------- Duty options logic ---------------- */
  const dutyOptions = useMemo(() => {
    if (!params?.duty) return [];

    // type_id === 3 → only duty_id === 2 allowed
    if (section.type_id === 3) {
      return params.duty.filter((d) => d.id === 2);
    }
    if (section.type_id === 2) {
      return params.duty.filter((d) => d.id === 1);
    }
    // default behavior
    return params.duty.filter((d) => d.id === 1 || d.id === 2);
  }, [params?.duty, section.type_id]);

  // Ensure valid duty_id when type_id changes
  useEffect(() => {
    if (
      dutyOptions.length > 0 &&
      !dutyOptions.some((d) => d.id === section.duty_id)
    ) {
      onFieldChange(dutyOptions[0].id, 'duty_id');
    }
  }, [dutyOptions, section.duty_id, onFieldChange]);

  useEffect(() => {
    const data = getConnectionHandId(
      tunnel_configuration,
      tunnelIndex,
      airflow_direction_id,
    );
    setIsConnHandEnabled(data.enable);
    if (!data.enable) {
      onFieldChange(data.value, 'conn_hand_id');
      setValue((draft) => {
        draft.tunnels[tunnelIndex].sections[sectionIndex].accessories.forEach(
          (a) => {
            if (a.type === 'drain_pan') a.conn_hand_id = data.value;
          },
        );
      });
    }
  }, [tunnel_configuration, airflow_direction_id]);

  /** ---------------- HT fluid options ---------------- */
  const htfluidOptions = useMemo(() => {
    return fluidsData && fluidsData.length > 0 ? fluidsData : [];
  }, [fluidsData]);

  useEffect(() => {
    if (htfluidOptions.length > 0) {
      const exist = htfluidOptions.find((e) => e.id === section.htfluid_id);
      if (!exist) {
        const item =  htfluidOptions.find((e) => e.id === 28) || htfluidOptions[0]
        onFieldChange(
          item?.id,
          'htfluid_id',
        );
        onFieldChange(
          item?.value,
          'htfluid',
        );
      }
    }
  }, [htfluidOptions]);

  return (
    <>
      <div className="bot-section__field">
        <span className="text-span">with a </span>
        <Select
          name="type_id"
          value={section.type_id}
          options={bot_coil_types}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase
          border_bottom={false}
        />
      </div>

      <div className="bot-section__field">
        <Select
          name="duty_id"
          value={section.duty_id}
          options={dutyOptions}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase
          border_bottom={false}
        />
      </div>

      <div className={`bot-section__field ${section.type_id === 3 && htfluidOptions.length < 1 ? 'd-none':''}`}>
        <Select
          name="htfluid_id"
          value={section.htfluid_id}
          options={htfluidOptions}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase
          border_bottom={false}
        />
        <span className="text-span">coil, with </span>
      </div>

      <div className="bot-section__field">
        <Select
          name="conn_hand_id"
          options={conn_hands}
          value={section.conn_hand_id}
          onChange={onFieldChange}
          className="bot-section__select"
          border_bottom={false}
          disabled={!isConnHandEnabled}
        />
        <span className="text-span">side connections,</span>
      </div>

      <div className="bot-section__field">
        <Select
          name="air_stream_id"
          options={ahu_params?.air.air_stream}
          value={section.air_stream_id}
          onChange={onFieldChange}
          className="bot-section__select"
          isLowerCase
          border_bottom={false}
          disabled
        />
        <span className="text-span">airstream</span>
      </div>
    </>
  );
};

export default memo(CoilSectionFields);
